import { v4 as uuidv4 } from 'uuid';
import {
  AgencyDetailsModel,
  LicenceDetails,
  LicenceDetailsState,
  UpdateAgencyDetails,
  UpdateAgencyDetailsAccountType,
} from '@urbanx/agx.agencies.client';
import {
  isValidABN,
  isValidACN,
  isValidEmail,
  isValidPhoneNumber,
} from '@urbanx/agx.ui-components2';
import { toDateOnlyString } from '@urbanx/agx.ui-components2';
import { CompanyLicenseDetails } from './types';

export type AgencyDetailsValidationErrors = Partial<
  Record<'abn' | 'acn' | 'listingsEmail' | 'listingsPhone', string>
>;

const getDigits = (value?: string): string => value?.replace(/\D/g, '') ?? '';

const hasValue = (value?: string): value is string => !!value?.trim();

export const stripCompanyLicenceUuid = (licence: CompanyLicenseDetails): LicenceDetails => {
  const { uuid, ...persistedLicence } = licence;

  void uuid;

  return {
    ...persistedLicence,
    expiryDate: toDateOnlyString(persistedLicence.expiryDate),
  };
};

export const withCompanyLicenceUuids = (licences: LicenceDetails[]): CompanyLicenseDetails[] =>
  licences.map((licence) => ({
    ...licence,
    uuid: uuidv4(),
  }));

export const getNextAvailableState = (
  companyLicences: CompanyLicenseDetails[]
): LicenceDetailsState | undefined => {
  const allStates = Object.values(LicenceDetailsState);

  return allStates.find(
    (state) => !companyLicences.map((licence) => licence.state).includes(state)
  );
};

export const createCompanyLicence = (
  state: LicenceDetailsState | undefined
): CompanyLicenseDetails => ({
  licenceNumber: '',
  uuid: uuidv4(),
  state,
  expiryDate: toDateOnlyString(new Date()),
});

export const sanitizeAgencyDetails = (
  agencyInformation: AgencyDetailsModel,
  companyLicences: CompanyLicenseDetails[]
): UpdateAgencyDetails => {
  const payload: UpdateAgencyDetails = {
    ...agencyInformation,
    accountType: agencyInformation.accountType as unknown as UpdateAgencyDetailsAccountType,
    companyDetails: {
      ...agencyInformation.companyDetails,
      licences: companyLicences.map(stripCompanyLicenceUuid),
    },
  };

  return payload;
};

export const validateAgencyDetails = (
  agencyInformation: AgencyDetailsModel
): AgencyDetailsValidationErrors => {
  const errors: AgencyDetailsValidationErrors = {};
  const abn = agencyInformation.companyDetails?.abn;
  const acn = agencyInformation.companyDetails?.acn;
  const listingsEmail = agencyInformation.listingsEmail;
  const listingsPhone = agencyInformation.listingsPhone;

  if (hasValue(abn) && !isValidABN(getDigits(abn))) {
    errors.abn = 'ABN provided is not valid';
  }

  if (hasValue(acn) && !isValidACN(getDigits(acn))) {
    errors.acn = 'ACN provided is not valid';
  }

  if (hasValue(listingsEmail) && !isValidEmail(listingsEmail.trim())) {
    errors.listingsEmail = 'Please specify a valid email address';
  }

  if (hasValue(listingsPhone) && !isValidPhoneNumber(listingsPhone)) {
    errors.listingsPhone = 'Phone / Mobile Number is not valid';
  }

  return errors;
};

export const hasAgencyDetailsValidationErrors = (errors: AgencyDetailsValidationErrors): boolean =>
  Object.values(errors).some(Boolean);

export const getLicenceStateOptions = (
  licence: CompanyLicenseDetails,
  companyLicences: CompanyLicenseDetails[],
  agencyStateOptions: Array<{ value: string; label: string }>
) =>
  agencyStateOptions.filter(
    (option) =>
      option.value === licence.state ||
      !companyLicences.some(
        (companyLicence) =>
          companyLicence.uuid !== licence.uuid && companyLicence.state?.toString() === option.value
      )
  );
